import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  createFilterOptions,
  MenuItem,
  Menu,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ChecklistIcon from "@mui/icons-material/Checklist";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CategoryIcon from "@mui/icons-material/Category";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BlockIcon from "@mui/icons-material/Block";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HistoryIcon from "@mui/icons-material/History";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { useMe } from "../MeContext";
import { useCurrentUser } from "../useCurrentUser";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import { exportInventoryItems, exportInventoryBatches } from "../exportExcel";
import { exportContentsPdf } from "../exportContentsPdf";
// The naming and section-building the printed contents sheet needs, shared with the
// public page a QR code on a container opens — so the two sheets stay identical.
import {
  cmpText,
  contentsSections as buildContentsSections,
  itemLabel as composeItemLabel,
  normaliseItem,
} from "../kitList";
import { downloadQrCode, downloadQrCodes } from "../qrCode";
import AllocationManager from "../components/AllocationManager";
import OutstandingAllocationsDialog from "../components/OutstandingAllocationsDialog";
import DuplicateContainerDialog from "../components/DuplicateContainerDialog";
import KitLinkDialog from "../components/KitLinkDialog";
import HoldingsPanel from "../components/stock/HoldingsPanel";
import ReceiveStockDialog from "../components/stock/ReceiveStockDialog";
import MoveStockDialog from "../components/stock/MoveStockDialog";
import DisposeStockDialog from "../components/stock/DisposeStockDialog";
import {
  BATCHED_CATEGORIES,
  CONTAINER_CATEGORY,
  INVENTORY_CATEGORIES,
  INVENTORY_CATEGORY_COLOR,
  STOCK_ROLE_LABEL,
  categoryHint,
  typeLabel,
} from "../constants";
// The expiry rule and the stock rules are shared with the stock pages, and are pinned
// down by stockRules.test.js.
import {
  EXPIRY_SOON_DAYS,
  allHoldings,
  checkVerdict,
  expiryFlag,
  graceDate,
  isStrictCategory,
  replenishPlan,
  roleOf,
  stockRoleOf,
  todayISO,
} from "../stockRules";

// Default matcher, so the "Add …" entry can be appended to the normal results.
const typeFilterOptions = createFilterOptions();

const ITEMS_API = `${API_BASE_URL}/inventory/items`;
const TYPES_API = `${API_BASE_URL}/inventory/types`;
const KITS_API = `${API_BASE_URL}/inventory/kits`;
// Read only to find when the last event finished — inventory is checked between events,
// so that date is what "a check is due" is measured against.
const EVENTS_API = `${API_BASE_URL}/events`;

// How far a save reaches when the item sits in a container that's part of a kit:
// across every instance of the kit, or in this container alone. Mirrors the `scope`
// field of InventoryItemCreate — the backend defaults to the cautious one, so this
// is always sent explicitly.
const SCOPE_KIT = "kit";
const SCOPE_ONE = "this_only";

// Dates and the expiry verdict (expiryFlag: red once past the printed date plus the
// type's grace, amber within EXPIRY_SOON_DAYS of it) come from stockRules.js, so the
// stock report and the kit check can't disagree with this page about what's expired.

// Amber isn't an MUI TextField state, so warning is painted on manually — same
// treatment as the Personnel page's expiry fields. `hint` is the helper text to
// keep when there's no flag to report — the grace itself, usually.
const expiryFieldProps = (iso, grace = 0, hint) => {
  const flag = expiryFlag(iso, grace);
  if (!flag) return hint ? { helperText: hint } : {};
  if (flag.severity === "error") return { error: true, helperText: flag.message };
  return {
    helperText: flag.message,
    sx: {
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
      "& .MuiFormHelperText-root": { color: "warning.main" },
    },
  };
};

// What the Expiry field says when the type allows a grace and the date is still
// inside it: the flags are silent, so the reason has to be on screen somewhere.
const graceHint = (iso, grace) =>
  grace ? `Held up to ${grace} year${grace === 1 ? "" : "s"} past this date`
          + `${iso ? ` — flagged from ${graceDate(iso, grace)}` : ""}`
        : undefined;

// Understocked: the item is meant to be kept at a level it has fallen below. For a
// batched item the quantity is the sum of its batches, rolled up server-side.
const isShort = (i) =>
  i.required_quantity != null && (i.quantity ?? 0) < i.required_quantity;

// Quantity in the items table — the actual figure, red when below what's required.
const QuantityCell = ({ item }) => {
  const short = isShort(item);
  const cell = (
    <TableCell align="right" sx={short ? { color: "error.main", fontWeight: 600 } : undefined}>
      {item.quantity}
    </TableCell>
  );
  return short ? <Tooltip title={`${item.required_quantity} required`}>{cell}</Tooltip> : cell;
};

// Expiry in the items table, coloured by severity — with a tooltip spelling the
// reason out, since colour alone isn't much of a signal. The date shown is always
// the printed one; `grace` only decides whether it's called out.
const ExpiryCell = ({ item, grace = 0 }) => {
  const flag = expiryFlag(item.expiry_date, grace);
  const cell = (
    <TableCell sx={flag ? { color: `${flag.severity}.main`, fontWeight: 600 } : undefined}>
      {item.expiry_date || "—"}
    </TableCell>
  );
  return flag ? <Tooltip title={flag.message}>{cell}</Tooltip> : cell;
};

const SORT_NAME = "Name";
const SORT_OPTIONS = [SORT_NAME, "Location", "Expiry date"];

// The ways an item needs attention — understocked, at (or near) its expiry date, or not
// checked since the last event finished. They're one filter facet rather than independent
// ones: selecting several shows items with any of those problems, since "what needs doing"
// is the question being asked, and the intersections are far too narrow to be worth a
// control of their own.
const ISSUE_SHORT = "short";
const ISSUE_EXPIRING = "expiring";
const ISSUE_CHECK_DUE = "check_due";
const ISSUE_LABEL = {
  [ISSUE_SHORT]: "Short",
  [ISSUE_EXPIRING]: "Expiring",
  [ISSUE_CHECK_DUE]: "Check due",
};
const ISSUE_HINT = {
  [ISSUE_SHORT]: "Only items stocked below their required quantity",
  [ISSUE_EXPIRING]: `Only items already expired, or expiring within ${EXPIRY_SOON_DAYS} days`
    + " — counted from the end of the type's expiry grace, where it has one",
};

// Columns in the items table, for the rows that span all of them (location, empty state).
// Both views have the same four — name, quantity, expiry, contents.
const ITEM_COLUMNS = 4;

// The two ways of listing items: everything at once, or nested inside its container.
const VIEW_FLAT = "flat";
const VIEW_TREE = "tree";

// Key under which the tree groups items that sit in no container — the roots.
const ROOT_KEY = 0;

// Indent per nesting level, in theme spacing units (so 3 = 24px), and the pixel width the
// expand toggle reserves so a leaf lines up with the containers beside it rather than
// sitting where their chevron does.
const TREE_INDENT = 3;
const TOGGLE_WIDTH = 30;

const BLANK = {
  type_id: "",
  serial_number: "",
  details: "",
  size: "",
  quantity: 1,
  required_quantity: "",
  expiry_date: "",
  last_checked_date: "",
  last_checked_by: "",
  location_id: "",
  notes: "",
};

// A stored item as editable form state — used by the item dialog and by the kit check,
// so both send the same shape to toPayload().
const formFromItem = (i) => ({
  type_id: i.type_id ?? "",
  serial_number: i.serial_number ?? "",
  details: i.details ?? "",
  size: i.size ?? "",
  quantity: i.quantity ?? 1,
  required_quantity: i.required_quantity ?? "",
  expiry_date: i.expiry_date ?? "",
  last_checked_date: i.last_checked_date ?? "",
  last_checked_by: i.last_checked_by ?? "",
  location_id: i.location_id ?? "",
  notes: i.notes ?? "",
});

export default function InventoryItems() {
  const { can } = useMe();
  const me = useCurrentUser(); // stamps "last checked by"
  const fullScreen = useFullScreenDialog();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [kits, setKits] = useState([]); // container families keeping the same contents
  const [events, setEvents] = useState([]); // only to find when the last one finished
  const [loading, setLoading] = useState(true); // true until the first items fetch resolves
  const [editForm, setEditForm] = useState(BLANK);
  const [editInitial, setEditInitial] = useState(BLANK); // edit-form snapshot, to detect unsaved changes
  const [editing, setEditing] = useState(null); // id being edited, "new" when adding, or null
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDelete, setConfirmDelete] = useState(false); // delete-confirmation prompt
  const [confirmDeleteRow, setConfirmDeleteRow] = useState(null); // item deleted from the kit check
  const [contentsStack, setContentsStack] = useState([]); // drill path of container items
  const [exportMenu, setExportMenu] = useState(null); // anchor for the export-format menu
  const [outstandingOpen, setOutstandingOpen] = useState(false); // allocations not returned
  // Batch-tracked stock (Drugs, Medical). `moveInit` opens the move dialog — prefilled
  // with lines and a destination, and set aside for disposal when `quarantine` — and
  // carries the kit-check row whose draft a completed move makes stale.
  const [stockMenu, setStockMenu] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [moveInit, setMoveInit] = useState(null);
  // Quick-add a type from the Type picker. Null when closed, otherwise the new type's
  // form; the item dialog adopts the type once it's saved.
  const [newType, setNewType] = useState(null);
  // Kit check: null when the contents dialog is just a read-only view, otherwise a draft
  // keyed by item id ({ quantity, required_quantity, batches }) covering the contents.
  const [check, setCheck] = useState(null);
  const [checked, setChecked] = useState({}); // ids ticked off during this pass
  const [checkFilter, setCheckFilter] = useState(""); // narrows the rows shown mid-check
  const [confirmExitCheck, setConfirmExitCheck] = useState(false);
  const [saving, setSaving] = useState(false); // a whole-dialog save (counts, container)
  const [savingId, setSavingId] = useState(null); // the row being ticked off
  // The item dialog's own writes — its Save creates an item, and "Checked" is the
  // same save with a stamp, so a double-tap on either would create two rows (see
  // useWriteLock). Distinct from `saving` above, which is the kit check's.
  const { busy: savingItem, run: runWrite } = useWriteLock();
  // Kits. `scope` is the add/promote choice in the item dialog; the other two hold the
  // container whose dialog is open.
  const [scope, setScope] = useState(SCOPE_KIT);
  const [duplicating, setDuplicating] = useState(null);
  const [linking, setLinking] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [confirmEmpty, setConfirmEmpty] = useState(false); // delete a container's contents
  // QR codes for the open container. `qr` holds the minted codes — the scanned
  // container's first, then its kit counterparts — so the dialog can show the URL
  // that is about to be printed before anything is downloaded.
  const [qr, setQr] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const navigate = useNavigate();

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [issues, setIssues] = useState([]); // ISSUE_SHORT / ISSUE_EXPIRING, empty for all items
  const [sortBy, setSortBy] = useState(SORT_NAME);

  // Flat list or tree. In the tree, `expanded` records only the containers the user has
  // explicitly opened or closed — see isExpanded() for why that's not a plain boolean map.
  const [view, setView] = useState(VIEW_TREE);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(ITEMS_API + "/").then(res => setItems(res.data)).finally(() => setLoading(false));
    axios.get(TYPES_API + "/").then(res => setTypes(res.data));
    axios.get(KITS_API + "/").then(res => setKits(res.data));
    axios.get(EVENTS_API + "/").then(res => setEvents(res.data));
  }, []);

  // A kit write touches rows all over the tree — items created in other containers,
  // required quantities brought into line — so the list is re-read rather than
  // patched from one response.
  const reload = () => Promise.all([
    axios.get(ITEMS_API + "/").then(res => setItems(res.data)),
    axios.get(KITS_API + "/").then(res => setKits(res.data)),
  ]);

  const typeById = Object.fromEntries(types.map(t => [t.id, t]));
  const itemById = Object.fromEntries(items.map(i => [i.id, i]));
  const kitById = Object.fromEntries(kits.map(k => [k.id, k]));

  const typeName = (id) => (typeById[id] ? typeLabel(typeById[id]) : `Type ${id}`);
  const typeCategory = (id) => typeById[id]?.category ?? "";
  const typeOptionLabel = (t) => `${typeLabel(t)} (${t.category})`;

  // Items have no name of their own, and their row id is internal — name them by their
  // details, or by their type when there are no details, then the serial number when there
  // is one, then the size after a slash: "Response jacket 7 / Men M 48", or just "Response
  // jacket 7" unsized. Takes an item or an edit-form (same field names), so dialog titles
  // can track live edits. The composition itself is in kitList.js, since the public
  // contents-list page names items the same way without access to the type list.
  const itemLabel = (i) => composeItemLabel(i, typeLabel(typeById[i.type_id]));

  // Alphabetical by the label above — the Name sort option, and the fixed order used by
  // the contents viewer and the kit check.
  const byName = (a, b) => cmpText(itemLabel(a), itemLabel(b));

  // The expiry grace of an item's type, in whole years
  // (inventory_types.expiry_grace_years): how long something of that type may be held
  // past its printed date before it is flagged. Anything falsy — unset, or a stored
  // 0 — is no grace, the behaviour every type had before the column existed. Takes an
  // item or an edit-form, like isBatched, so a dialog follows the type being picked.
  const graceFor = (f) => Number(typeById[f.type_id]?.expiry_grace_years) || 0;

  // Whether items of this type can be handed out to someone for an event
  // (inventory_types.can_allocate). Like the grace above it belongs to the type and
  // takes an item or an edit-form, so the dialog follows the type being picked. It
  // gates only *making* an allocation: the ones already recorded stay editable if
  // the flag is later taken off, so kit still out can be marked Returned.
  const allocatable = (f) => Boolean(typeById[f.type_id]?.can_allocate);

  // The type's free-text notes (inventory_types.notes): most often what to check about
  // something of this type — batteries, that it still works, a seal intact. Shown on the
  // kit check under the item's own line; blank is the common case and shows nothing.
  const typeNotes = (f) => (typeById[f.type_id]?.notes || "").trim();

  // Batched categories (Drugs, Medical) hold their stock as batches (stock holdings):
  // the batches carry the quantities, batch numbers and expiry dates, so the item
  // itself shows none of those three, and none of them is typed in — they change only
  // through a stock operation (core/stock.py). Every other category keeps them
  // directly on the item.
  const isBatched = (f) => BATCHED_CATEGORIES.includes(typeCategory(f.type_id));

  // Type is the only field an item must have: it can be identified by its type and
  // where it lives. Details and a serial number are how you tell several of the same
  // type in one place apart, so two items are only rejected when all four match.
  const canSave = (f) => Boolean(f.type_id);

  // Containers are simply the Storage-category items — bags, boxes and pouches.
  const isContainer = (i) => typeCategory(i.type_id) === CONTAINER_CATEGORY;

  // --- Checked since the last event ----------------------------------------------
  // Kit is checked between events, so an item's last-checked stamp only means anything
  // relative to the last event that finished: checked before it went out, and it has been
  // used since. An event with no end date falls back to its start date, and one still
  // running (or yet to come) isn't a reference point — nothing has happened after it yet.
  const eventEnd = (e) => e.end_date || e.start_date || "";
  const lastEvent = events
    .filter(e => eventEnd(e) && eventEnd(e) <= todayISO())
    // ISO dates sort correctly as plain strings, so the latest is the last one.
    .sort((a, b) => (eventEnd(a) < eventEnd(b) ? -1 : eventEnd(a) > eventEnd(b) ? 1 : 0))
    .pop() || null;
  const checkSince = lastEvent ? eventEnd(lastEvent) : null;
  const lastEventName = lastEvent?.label_long || lastEvent?.name || "the last event";

  // "success" checked since it ended, "warning" a check is due — and null when there's no
  // finished event to measure against, in which case nothing is claimed either way.
  const checkState = (i) => {
    if (!checkSince) return null;
    return i.last_checked_date && i.last_checked_date >= checkSince ? "success" : "warning";
  };

  // --- Kits ----------------------------------------------------------------------
  // Several containers keeping the same *required* contents while their actual stock
  // stays their own. Two fields carry it, read as a pair: kit_id with no kit_line is
  // one of the containers, both set is one line of the shared contents. See
  // backend/app/core/inventory_kits.py — this is only the reading of it, so the UI can
  // say what a save will affect before it happens.
  const kitName = (id) => kitById[id]?.name || "kit";
  const isKitInstance = (i) => Boolean(i?.kit_id) && !i?.kit_line;
  const isKitLine = (i) => Boolean(i?.kit_line);
  const kitInstances = (kitId) => items.filter(i => i.kit_id === kitId && !i.kit_line);
  // The rows in the other instances that this one is kept in step with.
  const counterparts = (i) =>
    isKitLine(i)
      ? items.filter(o => o.kit_id === i.kit_id && o.kit_line === i.kit_line && o.id !== i.id)
      : [];
  // An item inside a kit container that was deliberately left out of the kit.
  const isExtra = (i) =>
    !i.kit_id && Boolean(i.location_id) && Boolean(itemById[i.location_id]?.kit_id);
  // The kit a *new or edited* item would join, going by where the form puts it.
  const targetKitId = (f) => (f.location_id ? itemById[f.location_id]?.kit_id : null) || null;

  // An item as the dialog edits it. A batched item's stock isn't part of the form at
  // all — it is read off the item and changed through the stock operations.
  const itemForm = formFromItem;

  // Items that can hold other items. When editing, an item can't be its own container.
  const containerOptions = (excludeId) =>
    items.filter(i => isContainer(i) && i.id !== excludeId);

  // Chain of ancestor containers (outermost first), guarding against cycles.
  const locationPath = (item) => {
    const path = [];
    const seen = new Set([item.id]);
    let cur = item.location_id ? itemById[item.location_id] : null;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      path.unshift(cur);
      cur = cur.location_id ? itemById[cur.location_id] : null;
    }
    return path;
  };

  const locationPathString = (item) => locationPath(item).map(itemLabel).join(" / ");

  // Same trail, but including the item itself — "Van / Shelf 2 / Airway Bag" — so the
  // Location picker shows where each container sits, not just its own name.
  const itemPathLabel = (item) => [...locationPath(item), item].map(itemLabel).join(" / ");

  // Always alphabetical, whichever sort the list behind the dialog is using: the contents
  // viewer and the kit check are for working through a container's items one by one, and
  // that's easier against a fixed order you can predict.
  const contents = (containerId) =>
    items.filter(i => i.location_id === containerId).sort(byName);

  // Everything inside a container at any depth — what deleting or emptying it takes
  // with it, and so what the confirmation has to declare. Guards against a container
  // cycle, like locationPath above.
  const descendantsOf = (containerId) => {
    const out = [];
    const seen = new Set([containerId]);
    const stack = [containerId];
    while (stack.length > 0) {
      const parent = stack.pop();
      for (const child of items.filter(i => i.location_id === parent)) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        out.push(child);
        stack.push(child.id);
      }
    }
    return out;
  };

  // The printed contents list's sections, built by the shared kitList module off the
  // whole loaded item list. Mapping every item costs a pass, which is why this happens
  // on the click that prints rather than on render.
  const contentsSections = (container) => buildContentsSections(
    items.map(i => normaliseItem(i, typeLabel(typeById[i.type_id]), isContainer(i))),
    container.id,
  );

  // Read the container fresh rather than from the breadcrumb snapshot, so notes edited
  // since the dialog opened are the ones printed. Only this container's notes go on the
  // sheet — a pocket's or an item's own notes stay off it.
  const exportContents = (container) => {
    const c = itemById[container.id] ?? container;
    return exportContentsPdf({
      name: itemLabel(c),
      notes: c.notes,
      sections: contentsSections(c),
    });
  };

  // Open the contents viewer at a container, seeding the breadcrumb with its ancestors.
  const openContents = (container) => setContentsStack([...locationPath(container), container]);
  const closeContents = () => {
    setContentsStack([]);
    setCheck(null);
    setCheckFilter("");
    setConfirmExitCheck(false);
    setConfirmEmpty(false);
    setConfirmDeleteRow(null);
  };
  const drillInto = (container) => setContentsStack([...contentsStack, container]);
  const jumpTo = (index) => setContentsStack(contentsStack.slice(0, index + 1));

  // Coerce a form into the API payload: numbers where needed, null for blank optionals.
  // A batched item's quantity and expiry are roll-ups of its batches, which the
  // backend ignores on a save — they change only through the stock operations — so
  // the stored figures are simply sent back.
  // `itemScope` says how far a kit write reaches. It defaults to this container alone
  // so that everything reading a payload for comparison (the duplicate check, the kit
  // check) is unaffected by it — only an explicit save sends SCOPE_KIT.
  const toPayload = (f, itemScope = SCOPE_ONE) => {
    const batched = isBatched(f);
    return {
      type_id: Number(f.type_id),
      serial_number: batched ? null : (f.serial_number || null),
      details: f.details || null,
      size: f.size || null,
      quantity: f.quantity === "" ? (batched ? 0 : 1) : Number(f.quantity),
      required_quantity: String(f.required_quantity ?? "").trim() === "" ? null : Number(f.required_quantity),
      expiry_date: f.expiry_date || null,
      last_checked_date: f.last_checked_date || null,
      last_checked_by: f.last_checked_by || null,
      location_id: f.location_id === "" ? null : Number(f.location_id),
      notes: f.notes || null,
      scope: itemScope,
    };
  };

  // Two items of the same type in the same location must differ in details, size or
  // serial number, or there's nothing to tell them apart — a blank counts as a value,
  // so one with details and one without are fine. Size counts because it's part of the
  // displayed name: gloves M and gloves L in one pocket are two legible lines, not a
  // clash. Must match identity() in core/inventory_items.py, which enforces it; this
  // only saves a round trip.
  const identityKey = (x) => [
    x.type_id ?? "",
    x.location_id ?? "",
    (x.details || "").trim().toLowerCase(),
    (x.serial_number || "").trim().toLowerCase(),
    (x.size || "").trim().toLowerCase(),
  ].join("\u0000"); // NUL: a separator no user text can contain

  // The existing item a form would duplicate, if any. Built from the payload so the
  // comparison sees exactly what will be sent (a drug item's serial is per batch).
  const duplicateOf = (f, excludeId) => {
    if (!f.type_id) return null;
    const key = identityKey(toPayload(f));
    return items.find(i => i.id !== excludeId && identityKey(i) === key) || null;
  };

  const duplicateHint = (dup, f) =>
    `Same as ${itemLabel(dup)}, already in this location — give this one different details${
      isBatched(f) ? "" : " or a different serial number"}`;

  // Adding and editing share one dialog, as on the Personnel and Events pages: `editing`
  // is "new" while adding, so the title, Delete button and save call all key off it.
  // `locationId` prefills the container — how the contents viewer and the kit check add
  // something to the container they are already looking at, without making the user find
  // it again in the Location picker.
  const openNewIn = (locationId = "") => {
    const form = { ...BLANK, location_id: locationId };
    setEditForm(form);
    setEditInitial(form);
    // A new item in a kit container is nearly always something the whole kit needs.
    setScope(SCOPE_KIT);
    setEditing("new");
  };

  const openNew = () => openNewIn();

  const openEdit = (i) => {
    setEditing(i.id);
    const loaded = itemForm(i);
    setEditForm(loaded);
    setEditInitial(loaded);
    // An item that was deliberately left out of the kit stays out of it unless the
    // user asks otherwise — editing one must not quietly fan it out to every bag.
    setScope(SCOPE_ONE);
  };

  const closeEdit = () => { setEditing(null); setConfirmClose(false); setConfirmDelete(false); };

  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  const isDirty = () => JSON.stringify(editForm) !== JSON.stringify(editInitial);
  const requestClose = () => (isDirty() ? setConfirmClose(true) : closeEdit());

  // `overrides` lets a caller save fields it is setting in the same click, without
  // waiting for a setEditForm round-trip (see the Checked button).
  const saveEdit = (overrides = {}) => runWrite(() => {
    const form = { ...editForm, ...overrides };
    const payload = toPayload(form, scope);
    // A save that reaches other containers — a new line fanned out, a shared field
    // changed, a line moved — changes rows this page is holding, so the list is
    // re-read rather than patched from the one row that came back.
    const spreads = Boolean(targetKitId(form)) || isKitLine(itemById[editing]);
    if (editing === "new") {
      return axios.post(ITEMS_API + "/", payload).then(res => {
        if (spreads) reload();
        else setItems([...items, res.data]);
        closeEdit();
      });
    }
    return axios.put(`${ITEMS_API}/${editing}`, payload).then(res => {
      if (spreads) reload();
      else setItems(items.map(i => (i.id === editing ? res.data : i)));
      closeEdit();
    });
  });

  // "I've just checked this": stamp who and when, then save and close.
  const markChecked = () =>
    saveEdit({ last_checked_by: me.name || me.email || "", last_checked_date: todayISO() });

  const removeItem = () => {
    const target = itemById[editing];
    // The contents go too, which the confirmation has already spelled out — cascade is
    // opt-in server-side so that a delete can't quietly take more than was agreed.
    const inside = descendantsOf(editing).length;
    axios.delete(`${ITEMS_API}/${editing}`, { params: inside > 0 ? { cascade: true } : {} })
      .then(() => {
        // Deleting one line of a kit deletes it from every container, and a cascade
        // takes rows all over the tree, so both re-read the list; deleting a single
        // loose item only takes that item.
        if (isKitLine(target) || inside > 0) reload();
        else setItems(items.filter(i => i.id !== editing));
        closeEdit();
      });
  };

  // The same delete, from a row of the kit check. The list is always re-read: a kit line
  // goes from every container and a pocket takes its contents with it, so what changed is
  // never just the one row. The check itself stays open, minus that row.
  const removeCheckRow = () => {
    const target = confirmDeleteRow;
    const inside = descendantsOf(target.id).length;
    axios.delete(`${ITEMS_API}/${target.id}`, { params: inside > 0 ? { cascade: true } : {} })
      .then(() => {
        reload();
        setCheck(c => (c === null ? c : Object.fromEntries(
          Object.entries(c).filter(([id]) => Number(id) !== target.id))));
        setChecked(s => Object.fromEntries(
          Object.entries(s).filter(([id]) => Number(id) !== target.id)));
        setConfirmDeleteRow(null);
      });
  };

  // Delete everything inside a container, keeping the container. Local by design, even
  // for a kit member: see the empty endpoint in routers/inventory_item.py. The contents
  // dialog stays open, so the result is visible where the action was taken.
  const emptyContainer = () => {
    axios.post(`${ITEMS_API}/${currentItem.id}/empty`).then(() => {
      reload();
      setConfirmEmpty(false);
    });
  };

  // --- QR codes ---------------------------------------------------------------------
  // A sticker on the container, encoding a public URL that lists what should be inside
  // it. The backend mints and stores the token (core/kit_qr.py) and always returns the
  // counterparts too, so a kit's stickers can be printed in one run; the picture is
  // drawn here (qrCode.js).

  // Fetch (or mint) the codes for the open container and its kit counterparts, and open
  // the dialog on them. Nothing is downloaded yet: the URL is shown first, because it
  // is built from the backend's own origin and about to be printed onto something
  // permanent.
  const openQr = (container) => {
    setQrBusy(true);
    axios.post(`${ITEMS_API}/${container.id}/qr-codes`)
      .then(res => setQr({ itemId: container.id, codes: res.data || [] }))
      .finally(() => setQrBusy(false));
  };

  const closeQr = () => { if (!qrBusy) setQr(null); };

  // The code for the container that was open, out of the set that came back.
  const qrOwn = () => qr?.codes.find(c => c.item_id === qr.itemId) ?? qr?.codes[0];

  const saveQr = (codes, zipName) => {
    setQrBusy(true);
    const job = codes.length === 1
      ? downloadQrCode(codes[0])
      : downloadQrCodes(codes, zipName);
    // These draw in the browser rather than over the network, so a failure is a bug
    // or a blocked download and there's no interceptor to surface it.
    job.catch(err => notify(err?.message || "Could not save the QR code."))
       .finally(() => setQrBusy(false));
  };

  // Stop keeping a container (or one line) in step with the rest of its kit. Nothing
  // is deleted and no stock changes — the rows just stop being shared.
  const unlinkFromKit = () => {
    axios.post(`${ITEMS_API}/${editing}/unlink`).then(() => {
      reload();
      setConfirmUnlink(false);
      closeEdit();
    });
  };

  // --- Kit check -----------------------------------------------------------------
  // Counting a container's contents in one pass. Items are ticked off one at a time —
  // that's how a kit is actually checked, working through what's physically there and
  // seeing what's left — so each tick saves that item straight away and stamps it as
  // checked. Nested containers aren't counted: drill into one and check it separately.
  //
  // Two kinds of row. An ordinary item's count and expiry are fields on the item, saved
  // with the item (PUT, InventoryItems.edit) as they always were. A batch-tracked one
  // (Drugs, Medical) is counted batch by batch against what the record says is there,
  // and saved as a stock check (POST /stock/check, Stock.check) — which is what puts the
  // count, and any difference, on each batch's history. A difference on a drug needs a
  // reason and is flagged for investigation; a medical consumable that's short was
  // simply used. See checkVerdict in stockRules.js and check() in core/stock.py.

  // A row's draft. `counts` (keyed by holding id) start at the record, so an untouched
  // row is a matching count; `found` is batches turned up that the record doesn't have
  // here. The required quantity is in here too because it's editable mid-check: unlike
  // the counts it's part of what the kit is *meant* to hold, so saving one propagates
  // to every container.
  const checkDraft = (i) => ({
    quantity: i.quantity ?? "",
    required_quantity: i.required_quantity ?? "",
    expiry_date: i.expiry_date ?? "",
    counts: Object.fromEntries((i.batches || []).map(h => [h.id, String(h.quantity)])),
    reasons: {},
    found: [],
  });

  // Start a check on a container, given explicitly rather than read from the breadcrumb:
  // the list's own kit-check button opens the dialog and starts the check in one click,
  // so `current` isn't set yet at that point.
  const startCheckIn = (container) => {
    const rows = contents(container.id);
    setCheck(Object.fromEntries(rows.map(i => [i.id, checkDraft(i)])));
    setCheckFilter("");
    // Anything already stamped today counts as done, so closing and reopening the dialog
    // mid-pass doesn't lose your place.
    setChecked(Object.fromEntries(rows.filter(i => i.last_checked_date === todayISO()).map(i => [i.id, true])));
  };

  const startCheck = () => startCheckIn(current);

  // Straight into the check from the items list, skipping the read-only contents view —
  // checking a bag is the reason most people open one at all.
  const openCheck = (container) => {
    openContents(container);
    startCheckIn(container);
  };

  // A row's draft, falling back to the stored item for something added to the container
  // since the check began — either here, or in another container of the same kit — and
  // for a row whose draft was dropped once it saved (see dropDraft).
  const blankDraft = { quantity: "", required_quantity: "", expiry_date: "", counts: {}, reasons: {}, found: [] };
  const draftOf = (id) => check?.[id] ?? (itemById[id] ? checkDraft(itemById[id]) : blankDraft);
  const withDraft = (c, id, fn) => ({ ...c, [id]: fn(c[id] ?? draftOf(id)) });

  const patchCheck = (id, patch) => setCheck(c => withDraft(c, id, d => ({ ...d, ...patch })));
  const patchCount = (id, holdingId, value) =>
    setCheck(c => withDraft(c, id, d => ({ ...d, counts: { ...d.counts, [holdingId]: value } })));
  const patchReason = (id, holdingId, value) =>
    setCheck(c => withDraft(c, id, d => ({ ...d, reasons: { ...d.reasons, [holdingId]: value } })));
  const addFound = (id) => setCheck(c => withDraft(c, id, d => ({
    ...d, found: [...d.found, { key: Date.now(), batch_number: "", expiry_date: "", counted: "", reason: "" }],
  })));
  const patchFound = (id, key, patch) => setCheck(c => withDraft(c, id, d => ({
    ...d, found: d.found.map(f => (f.key === key ? { ...f, ...patch } : f)),
  })));
  const removeFound = (id, key) => setCheck(c => withDraft(c, id, d => ({
    ...d, found: d.found.filter(f => f.key !== key),
  })));

  // Once a batch-tracked row has saved, its holdings are re-read and may have new ids
  // (a batch counted to nothing is gone, a found one is new), so its draft is rebuilt
  // from the fresh item rather than kept.
  const dropDraft = (id) => setCheck(c => {
    if (c === null) return c;
    const next = { ...c };
    delete next[id];
    return next;
  });

  // Who may save a row: whoever may save what it changes.
  const canCheckRow = (i) => (isBatched(i) ? can("Stock.check") : can("InventoryItems.edit"));
  const strictFor = (i) => isStrictCategory(typeCategory(i.type_id));

  // Each counted batch of a row, with the verdict the server will reach on it.
  const batchVerdicts = (i) => {
    const d = draftOf(i.id);
    return (i.batches || []).map(h => {
      const counted = d.counts[h.id] ?? String(h.quantity);
      return { h, counted, reason: d.reasons[h.id] ?? "",
               ...checkVerdict(h.quantity, counted, strictFor(i)) };
    });
  };
  const foundLines = (i) => draftOf(i.id).found.filter(f => Number(f.counted) >= 1);

  // What a batched row adds up to as counted: its batches plus anything found.
  const countedTotal = (i) =>
    batchVerdicts(i).reduce((n, v) => n + (Number(v.counted) || 0), 0)
    + foundLines(i).reduce((n, f) => n + Number(f.counted), 0);

  const blankToNull = (v) => (String(v ?? "").trim() === "" ? null : Number(v));
  const requiredChanged = (i) =>
    blankToNull(draftOf(i.id).required_quantity) !== (i.required_quantity ?? null);

  // Why a batched row can't be ticked yet, or null.
  const rowProblem = (i) => {
    if (!isBatched(i)) return null;
    const verdicts = batchVerdicts(i);
    if (verdicts.some(v => v.kind === null)) return "Count every batch";
    if (verdicts.some(v => v.needsReason && !v.reason.trim())) return "Say why the count differs from the record";
    const strict = strictFor(i);
    if (strict && foundLines(i).some(f => !f.batch_number.trim())) return "Stock found needs its batch number";
    if (strict && foundLines(i).some(f => !f.reason.trim())) return "Say where the stock found came from";
    return null;
  };

  const checkedStamp = () => ({ last_checked_by: me.name || me.email || "", last_checked_date: todayISO() });

  // What an ordinary row would send, so "changed?" compares like with like. A cleared
  // count here means none were found — not toPayload's "unset, so 1".
  const checkPayload = (i, stamp) => {
    const draft = draftOf(i.id);
    return toPayload({
      ...itemForm(i),
      quantity: draft.quantity,
      required_quantity: draft.required_quantity,
      expiry_date: draft.expiry_date,
      ...(String(draft.quantity ?? "").trim() === "" ? { quantity: 0 } : {}),
      ...(stamp ? checkedStamp() : {}),
    });
  };

  const checkEdited = (i) => (isBatched(i)
    ? requiredChanged(i) || foundLines(i).length > 0
      || batchVerdicts(i).some(v => v.kind !== "check")
    : JSON.stringify(checkPayload(i, false)) !== JSON.stringify(toPayload(itemForm(i))));

  // Only unticked rows can hold unsaved work: ticking a row saves it.
  const uncountedRows = () => contents(current.id).filter(i => !checked[i.id] && checkEdited(i));
  const checkDirty = () => check != null && uncountedRows().length > 0;

  const checkedCount = () => contents(current.id).filter(i => checked[i.id]).length;
  // An empty container counts as fully checked: a check can be started on one so that
  // what belongs in it can be added from here, and if nothing was added then looking
  // inside and finding it bare is still a check.
  const allChecked = () => contents(current.id).every(i => checked[i.id]);

  const exitCheck = () => {
    setCheck(null);
    setChecked({});
    setCheckFilter("");
    setConfirmExitCheck(false);
  };
  const requestExitCheck = () => (checkDirty() ? setConfirmExitCheck(true) : exitCheck());

  const applySaved = (responses) => {
    const saved = Object.fromEntries(responses.map(r => [r.data.id, r.data]));
    setItems(list => list.map(i => saved[i.id] ?? i));
  };

  // A required quantity is part of the kit's shared spec, so saving a changed one is
  // applied to the counterpart in every other container by the backend — rows this page
  // is holding, and none of them the one that comes back in the response.
  const spreadsRequired = (i) => isKitLine(i) && requiredChanged(i);

  // Save one row: an ordinary item as an item, a batch-tracked one as a stock check
  // (after its required quantity, when that changed and the user may change it).
  // Resolves to whether the check was flagged for investigation.
  const saveRow = (i, stamp) => {
    if (!isBatched(i)) {
      return axios.put(`${ITEMS_API}/${i.id}`, checkPayload(i, stamp))
        .then(res => ({ saved: res, investigate: false }));
    }
    const put = requiredChanged(i) && can("InventoryItems.edit")
      ? axios.put(`${ITEMS_API}/${i.id}`,
                  toPayload({ ...itemForm(i), required_quantity: draftOf(i.id).required_quantity }))
      : Promise.resolve();
    return put.then(() => axios.post(`${API_BASE_URL}/stock/check`, {
      container_id: current.id,
      lines: batchVerdicts(i).map(v => ({ holding_id: v.h.id, counted: Number(v.counted),
                                          reason: v.reason.trim() || null })),
      found: foundLines(i).map(f => ({ item_id: i.id, batch_number: f.batch_number.trim() || null,
                                       expiry_date: f.expiry_date || null,
                                       counted: Number(f.counted), reason: f.reason.trim() || null })),
      stamp_item_ids: stamp ? [i.id] : [],
    })).then(res => ({ saved: null, investigate: Boolean(res.data?.needs_investigation) }));
  };

  // After rows saved: batch-tracked rows and spreading required quantities need the
  // list re-read; ordinary ones can be patched from their responses.
  const afterSave = (rows, results) => {
    const needsReload = rows.some(i => isBatched(i) || spreadsRequired(i));
    if (results.some(r => r.investigate)) {
      notify("Count recorded. The difference has been flagged for investigation.", "warning");
    }
    if (!needsReload) {
      applySaved(results.map(r => r.saved).filter(Boolean));
      return Promise.resolve();
    }
    return reload().then(() => rows.filter(isBatched).forEach(i => dropDraft(i.id)));
  };

  // Tick one item off: save whatever it was counted as, stamped with who and when.
  const checkOffItem = (i) => {
    setSavingId(i.id);
    saveRow(i, true)
      .then(result => afterSave([i], [result]))
      .then(() => setChecked(c => ({ ...c, [i.id]: true })))
      .catch(() => {})
      .finally(() => setSavingId(null));
  };

  // Reopen a ticked row for editing. The stamp it already has stands until it's ticked
  // again — this only makes the fields editable, it doesn't undo the save.
  const reopenItem = (id) => setChecked(c => ({ ...c, [id]: false }));

  // Persist counts on rows that were edited but not ticked, without stamping them.
  // A row that can't be saved yet (a difference with no reason) is left for the user.
  const saveCounts = () => {
    const rows = uncountedRows().filter(i => canCheckRow(i) && !rowProblem(i));
    if (rows.length === 0) return;
    setSaving(true);
    Promise.all(rows.map(i => saveRow(i, false)))
      .then(results => afterSave(rows, results))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  // Finish: stamp the container itself, once everything inside it has been ticked. A
  // stock check stamps it when the user may make one, so a Clinical Manager can finish
  // a drugs pouch; otherwise it's saved on the item, as it always was.
  const markContainerChecked = () => {
    const container = itemById[current.id] ?? current;
    setSaving(true);
    const save = can("Stock.check")
      ? axios.post(`${API_BASE_URL}/stock/check`, { container_id: container.id, stamp_container: true })
          .then(() => reload())
      : axios.put(`${ITEMS_API}/${container.id}`,
                  toPayload({ ...itemForm(container), ...checkedStamp() }))
          .then(res => applySaved([res]));
    save.then(() => exitCheck()).catch(() => {}).finally(() => setSaving(false));
  };

  // Top a short line up from the stock cupboards, earliest expiry first (never expired
  // or recalled stock). Opens the move dialog prefilled, for the user to confirm or
  // change; the row's draft is rebuilt once the move is done.
  const replenish = (i) => {
    const need = Math.max(0, (blankToNull(draftOf(i.id).required_quantity) ?? 0) - (i.quantity ?? 0));
    const sources = allHoldings(items)
      .filter(h => h.item.type_id === i.type_id && stockRoleOf(h.item, itemById, typeById) === "store");
    const plan = replenishPlan(sources, need, { grace: graceFor(i) });
    if (plan.lines.length === 0) {
      notify("There's none of this in date in the stock cupboards to top up from.", "warning");
      return;
    }
    if (plan.unmet > 0) {
      notify(`The stock cupboards are ${plan.unmet} short of what's needed — moving what there is.`, "warning");
    }
    setMoveInit({ toContainerId: i.location_id, lines: plan.lines, rowId: i.id });
  };

  // The rows on screen mid-check. The filter is only ever about finding the one row you
  // came to change in a bag of forty, so it narrows what's shown and nothing else:
  // progress, unsaved counts and "everything ticked?" are all still about the whole
  // container, since a filtered-out row is hidden, not excused.
  const checkRows = () => {
    const q = checkFilter.trim().toLowerCase();
    const rows = contents(current.id);
    return q === "" ? rows : rows.filter(i =>
      itemLabel(i).toLowerCase().includes(q) || typeName(i.type_id).toLowerCase().includes(q));
  };

  // A batch-tracked row's batches, counted one by one, plus any found that the record
  // doesn't have here. A render function so the inputs keep focus while typing.
  const batchCountTable = (i) => {
    const grace = graceFor(i);
    const strict = strictFor(i);
    const draft = draftOf(i.id);
    return (
      <Box>
        {(i.batches || []).length === 0 && draft.found.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No batches recorded here.
          </Typography>
        )}
        <Stack spacing={1}>
          {batchVerdicts(i).map(v => {
            const flag = expiryFlag(v.h.expiry_date, grace);
            const differs = v.kind && v.kind !== "check";
            return (
              <Stack key={v.h.id} direction={{ xs: "column", sm: "row" }} spacing={1}
                     sx={{ alignItems: { sm: "flex-start" } }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="body2" component="div">
                    Batch {v.h.serial_number || "not recorded"}
                    {v.h.recalled && <Chip size="small" color="error" label="Recalled" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block",
                    color: flag ? `${flag.severity}.main` : "text.secondary" }}>
                    expires {v.h.expiry_date || "—"}{flag ? ` — ${flag.message}` : ""} · record says {v.h.quantity}
                    {v.h.opened ? ` (${v.h.opened} opened)` : ""}
                  </Typography>
                </Box>
                <TextField label="Counted" type="number" size="small" sx={{ width: 100 }}
                           value={v.counted} error={v.kind === null}
                           slotProps={{ htmlInput: { min: 0, step: 1 } }}
                           onChange={e => patchCount(i.id, v.h.id, e.target.value)} />
                {differs && (
                  <TextField size="small" sx={{ flex: 1, minWidth: 200 }}
                             label={v.needsReason ? "Why it differs" : "Note (optional)"}
                             required={v.needsReason}
                             error={v.needsReason && !v.reason.trim()}
                             helperText={v.kind === "consume"
                               ? "Recorded as used"
                               : v.investigate ? "Will be flagged for investigation" : undefined}
                             value={v.reason}
                             onChange={e => patchReason(i.id, v.h.id, e.target.value)} />
                )}
              </Stack>
            );
          })}
          {draft.found.map(f => (
            <Stack key={f.key} direction={{ xs: "column", sm: "row" }} spacing={1}
                   sx={{ alignItems: { sm: "flex-start" } }}>
              <TextField label="Batch number" size="small" sx={{ flex: 1, minWidth: 120 }}
                         required={strict} value={f.batch_number}
                         onChange={e => patchFound(i.id, f.key, { batch_number: e.target.value })} />
              <TextField label="Expiry date" type="date" size="small" sx={{ width: 160 }}
                         slotProps={{ inputLabel: { shrink: true } }} value={f.expiry_date}
                         onChange={e => patchFound(i.id, f.key, { expiry_date: e.target.value })} />
              <TextField label="Found" type="number" size="small" sx={{ width: 90 }}
                         slotProps={{ htmlInput: { min: 1, step: 1 } }} value={f.counted}
                         onChange={e => patchFound(i.id, f.key, { counted: e.target.value })} />
              <TextField label={strict ? "Where it came from" : "Note (optional)"} size="small"
                         sx={{ flex: 1, minWidth: 160 }} required={strict} value={f.reason}
                         onChange={e => patchFound(i.id, f.key, { reason: e.target.value })} />
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => removeFound(i.id, f.key)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={() => addFound(i.id)}>
              Add a batch that isn't listed
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  };

  // The kit check itself: a row per item with what's required against what's actually
  // there, batch-tracked items opening out into their batches underneath.
  const kitCheckTable = () => {
    const rows = checkRows();
    const canEditRequired = can("InventoryItems.edit");
    return (
      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 700 } }}>
            <TableCell sx={{ width: 44 }} />
            <TableCell>Item</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Required</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Actual</TableCell>
            <TableCell sx={{ width: 170 }}>Expiry</TableCell>
            <TableCell align="right" sx={{ width: 150 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                {contents(current.id).length === 0
                  ? "This container is empty."
                  : "No items here match that filter."}
              </TableCell>
            </TableRow>
          )}
          {rows.map(i => {
            const draft = draftOf(i.id); // may be an item added since the check began
            const batched = isBatched(i);
            const done = Boolean(checked[i.id]);
            const busy = savingId === i.id;
            const editable = canCheckRow(i);
            const total = batched ? countedTotal(i) : (Number(draft.quantity) || 0);
            // The draft's figure, not the stored one: it's editable in this table, and
            // what's short has to be measured against what the user has just typed.
            const required = blankToNull(draft.required_quantity);
            const short = required != null && total < required;
            // A batched item's expiry is the soonest of its batches; anything else carries
            // its own and can be retyped here — replacing an out-of-date dressing is half
            // of what a check turns up.
            const expiry = batched ? (i.expiry_date || "") : draft.expiry_date;
            const grace = graceFor(i);
            const flag = expiryFlag(expiry, grace);
            const expiryProps = expiryFieldProps(expiry, grace);
            const notes = typeNotes(i);
            const problem = done ? null : rowProblem(i);
            // Topping up from the cupboard is offered on a line whose *record* is short
            // — once it's been counted, so the count isn't confused by the move.
            const canReplenish = batched && done && isShort(i) && can("Stock.move")
              && stockRoleOf(i, itemById, typeById) !== "store";
            return [
              // A ticked row goes quiet: greyed, read-only, and out of the way of what's left.
              <TableRow key={i.id}
                        sx={{
                          ...((batched && !done) || notes ? { "& td": { borderBottom: "none" } } : {}),
                          ...(done ? { bgcolor: "action.hover" } : {}),
                        }}>
                <TableCell>
                  {done && <TaskAltIcon fontSize="small" color="success" />}
                </TableCell>
                <TableCell sx={done ? { color: "text.secondary" } : undefined}>
                  {itemLabel(i)}
                  {isContainer(i) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Container · {contents(i.id).length} inside · check separately
                    </Typography>
                  )}
                  {done && batched && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {(i.batches || []).length} batch{(i.batches || []).length === 1 ? "" : "es"}
                    </Typography>
                  )}
                  {problem && (
                    <Typography variant="caption" color="error" sx={{ display: "block" }}>{problem}</Typography>
                  )}
                </TableCell>
                {/* Editable in place: a check is when someone notices the bag should be
                    carrying four of these, not two, and the answer belongs to the kit
                    rather than to this bag — see the note under the breadcrumb. */}
                <TableCell align="right">
                  {done || !canEditRequired ? (required ?? "—") : (
                    <TextField type="number" size="small" sx={{ width: 80 }}
                               value={draft.required_quantity}
                               onChange={e => patchCheck(i.id, { required_quantity: e.target.value })} />
                  )}
                </TableCell>
                <TableCell align="right">
                  {done || batched || !editable ? (
                    // Ticked off, or added up from the batches below — either way, text.
                    <Typography component="span"
                                sx={short ? { color: "error.main", fontWeight: 600 } : undefined}>
                      {done ? (i.quantity ?? 0) : total}
                    </Typography>
                  ) : (
                    <TextField type="number" size="small" sx={{ width: 90 }} error={short}
                               value={draft.quantity}
                               onChange={e => patchCheck(i.id, { quantity: e.target.value })} />
                  )}
                </TableCell>
                <TableCell>
                  {done || batched || !editable ? (
                    <>
                      <Typography variant="body2"
                                  sx={flag ? { color: `${flag.severity}.main`, fontWeight: 600 } : undefined}>
                        {expiry || "—"}
                      </Typography>
                      {flag && (
                        <Typography variant="caption"
                                    sx={{ color: `${flag.severity}.main`, display: "block" }}>
                          {flag.message}
                        </Typography>
                      )}
                    </>
                  ) : (
                    // expiryFieldProps carries its own sx for the amber state, so the
                    // width is merged into it rather than being overwritten by it.
                    <TextField type="date" size="small"
                               slotProps={{ inputLabel: { shrink: true } }}
                               value={draft.expiry_date}
                               {...expiryProps}
                               sx={{ width: 150, ...expiryProps.sx }}
                               onChange={e => patchCheck(i.id, { expiry_date: e.target.value })} />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                    {canReplenish && (
                      <Tooltip title="Top this up from the stock cupboards, earliest expiry first">
                        <Button size="small" onClick={() => replenish(i)}>Top up</Button>
                      </Tooltip>
                    )}
                    {done ? (
                      editable && <Button size="small" onClick={() => reopenItem(i.id)}>Recount</Button>
                    ) : editable && (
                      <Button size="small" variant="outlined" startIcon={<TaskAltIcon />}
                              disabled={busy || Boolean(problem)} onClick={() => checkOffItem(i)}>
                        {busy ? "Saving" : "Checked"}
                      </Button>
                    )}
                    {/* A check is also where you find out something shouldn't be in the
                        bag at all. Confirmed first, and the confirmation says how far it
                        reaches — one line of a kit goes from every container. */}
                    {can("InventoryItems.delete") && (
                      <Tooltip title="Delete this item">
                        <IconButton size="small" onClick={() => setConfirmDeleteRow(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>,
              // Unlike the batch counts below, this stays on a ticked row: it is the type
              // speaking about the item, not something left to do.
              notes !== "" && (
                <TableRow key={`${i.id}-notes`}
                          sx={{
                            ...(batched && !done ? { "& td": { borderBottom: "none" } } : {}),
                            ...(done ? { bgcolor: "action.hover" } : {}),
                          }}>
                  <TableCell />
                  <TableCell colSpan={5} sx={{ pt: 0 }}>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: "block", whiteSpace: "pre-line" }}>
                      {notes}
                    </Typography>
                  </TableCell>
                </TableRow>
              ),
              batched && !done && editable && (
                <TableRow key={`${i.id}-batches`}>
                  <TableCell />
                  <TableCell colSpan={5} sx={{ pt: 0 }}>
                    {batchCountTable(i)}
                    {strictFor(i) || (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Fewer than recorded is taken as used.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ),
            ];
          })}
        </TableBody>
      </Table>
    );
  };

  // Quick-add a type without leaving the item you're entering: name comes prefilled
  // from what was typed, category is required (as on the Types page), and the rest of a
  // type's fields (notes, reorder details) are filled in later on that page.
  const openNewType = (name) => setNewType({ name, category: "" });

  const saveNewType = () => runWrite(() =>
    axios.post(TYPES_API + "/", newType).then(res => {
      setTypes([...types, res.data]);
      setEditForm(f => ({ ...f, type_id: res.data.id }));
      setNewType(null);
    }));

  // Type/location pickers filter as you type; clearing them yields "" (no type / no container).
  // Plain functions rather than components, so a parent re-render doesn't remount the
  // Autocomplete and wipe whatever the user has half-typed into it.
  const byId = (o, v) => o.id === v.id;

  // Typing a type that doesn't exist yet offers to create it, rather than dead-ending
  // on "No options" and sending the user off to the Types page mid-entry.
  // `focus` autofocuses the input — Autocomplete has no autoFocus prop of its own, it
  // belongs on the TextField that renderInput produces.
  const typePicker = ({ value, onPick, focus = false, ...props }) => (
    <Autocomplete
      options={types}
      getOptionLabel={(t) => (t.addName ? `Add "${t.addName}"` : typeOptionLabel(t))}
      getOptionKey={(t) => t.id ?? "add-new"}
      isOptionEqualToValue={byId}
      value={typeById[value] ?? null}
      filterOptions={(options, state) => {
        const matches = typeFilterOptions(options, state);
        const typed = state.inputValue.trim();
        // Only when nothing matches exactly — no point offering to add "Bandage" twice.
        const exists = options.some(t => t.name?.toLowerCase() === typed.toLowerCase());
        return typed && !exists && can("InventoryTypes.add")
          ? [...matches, { addName: typed }]
          : matches;
      }}
      onChange={(_, t) => {
        if (t?.addName) openNewType(t.addName);
        else onPick(t ? t.id : "");
      }}
      renderInput={(p) => <TextField {...p} label="Type" required autoFocus={focus} />}
      {...props}
    />
  );

  const locationPicker = ({ value, excludeId, onPick, helperText, ...props }) => {
    // Sorted by full path, so nested containers list under their parent.
    const options = containerOptions(excludeId)
      .sort((a, b) => itemPathLabel(a).localeCompare(itemPathLabel(b)));
    return (
      <Autocomplete
        options={options}
        getOptionLabel={itemPathLabel}
        getOptionKey={(c) => c.id}  // labels can repeat (same type, no details/serial)
        isOptionEqualToValue={byId}
        value={itemById[value] ?? null}
        onChange={(_, c) => onPick(c ? c.id : "")}
        renderInput={(p) => (
          <TextField {...p} label="Location (container)"
                     helperText={options.length === 0 ? "No container items defined yet" : helperText} />
        )}
        {...props}
      />
    );
  };

  // Distinct categories present across the defined types, for the filter dropdown.
  const categories = [...new Set(types.map(t => t.category).filter(Boolean))].sort();

  // One box against the name the listing shows. The type name is matched as well, because an
  // item with details of its own doesn't display it — and "every bandage, whatever it's
  // called" is still a search worth being able to make.
  const matchesName = (i) => {
    const q = nameFilter.trim().toLowerCase();
    return q === "" ||
      itemLabel(i).toLowerCase().includes(q) ||
      typeName(i.type_id).toLowerCase().includes(q);
  };

  // Nothing selected means no restriction; otherwise the item must have one of the problems
  // asked for. A batched item's quantity and expiry are rolled up from its packs
  // server-side, so both tests read true figures for drugs too.
  const matchesIssues = (i) =>
    issues.length === 0 ||
    (issues.includes(ISSUE_SHORT) && isShort(i)) ||
    (issues.includes(ISSUE_EXPIRING) && expiryFlag(i.expiry_date, graceFor(i)) !== null) ||
    (issues.includes(ISSUE_CHECK_DUE) && checkState(i) === "warning");

  // The issues that can be filtered on. Check due needs a finished event to be measured
  // against, so it isn't offered when there is none — better than a button that always
  // comes back empty.
  const issueOptions = [ISSUE_SHORT, ISSUE_EXPIRING, ...(checkSince ? [ISSUE_CHECK_DUE] : [])];
  const issueHint = (issue) =>
    issue === ISSUE_CHECK_DUE
      ? `Only items not checked since ${lastEventName} ended (${checkSince})`
      : ISSUE_HINT[issue];

  const matchesFilters = (i) =>
    (categoryFilter === "" || typeCategory(i.type_id) === categoryFilter) &&
    matchesName(i) &&
    locationPathString(i).toLowerCase().includes(locationFilter.toLowerCase()) &&
    matchesIssues(i);

  const filtered = items.filter(matchesFilters);

  const anyFilter = Boolean(categoryFilter || nameFilter || locationFilter || issues.length);

  // Sort comparators, keyed by the Sort by option. Each falls back to the display
  // name so equal keys (a shared container, no expiry) still come out in a stable
  // order. Top-level items have an empty location path, so they lead that sort.
  const COMPARATORS = {
    [SORT_NAME]: byName,
    Location: (a, b) => cmpText(locationPathString(a), locationPathString(b)) || byName(a, b),
    "Expiry date": (a, b) =>
      (a.expiry_date ? 0 : 1) - (b.expiry_date ? 0 : 1) ||   // undated last, not first
      cmpText(a.expiry_date || "", b.expiry_date || "") ||
      byName(a, b),
  };

  const visible = [...filtered].sort(COMPARATORS[sortBy]);

  // --- Tree view -----------------------------------------------------------------
  // The same items, nested: the top level is everything in no container (top-level
  // containers included), and each container opens out into its own contents.

  // Contents by container id, in one pass rather than a scan per node. An item whose
  // container has been deleted from under it is grouped as a root, so it can't vanish.
  const childMap = {};
  items.forEach(i => {
    const key = i.location_id && itemById[i.location_id] ? i.location_id : ROOT_KEY;
    (childMap[key] ||= []).push(i);
  });

  // Sorted, filtered nodes under one container. A container survives the filters when it
  // matches them itself *or* when something inside it does at any depth — otherwise a
  // filter would hide the path to its own hits. `path` guards against a container cycle.
  const treeNodes = (parentId, path) => {
    const nodes = [];
    for (const i of childMap[parentId] || []) {
      if (path.has(i.id)) continue;
      path.add(i.id);
      const children = isContainer(i) ? treeNodes(i.id, path) : [];
      path.delete(i.id);
      if (matchesFilters(i) || children.length > 0) nodes.push({ item: i, children });
    }
    return nodes.sort((a, b) => COMPARATORS[sortBy](a.item, b.item));
  };

  const tree = view === VIEW_TREE ? treeNodes(ROOT_KEY, new Set()) : [];

  // Collapsed by default, but open by default once a filter is on — otherwise filtering
  // would leave the matches buried inside containers the user has to open by hand. Either
  // way an explicit click wins, which is why `expanded` holds true/false, not just true.
  const isExpanded = (id) => (anyFilter ? expanded[id] !== false : expanded[id] === true);
  const toggleExpanded = (id) => setExpanded(e => ({ ...e, [id]: !isExpanded(id) }));

  // The visible rows, depth-first: a node's children only follow it while it's open.
  const treeRows = (nodes, depth = 0) =>
    nodes.flatMap(n => [
      { ...n, depth },
      ...(isExpanded(n.item.id) ? treeRows(n.children, depth + 1) : []),
    ]);

  const treeVisible = treeRows(tree);

  // Breadcrumb trail of an item's location hierarchy; crumbs open that container's contents.
  const LocationCrumbs = ({ item }) => {
    const path = locationPath(item);
    if (path.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
      <Breadcrumbs separator="›" sx={{ fontSize: 13 }} onClick={e => e.stopPropagation()}>
        {path.map(c => (
          <Link key={c.id} component="button" type="button" underline="hover"
                color="text.secondary" sx={{ fontSize: 13 }}
                onClick={() => openContents(c)}>
            {itemLabel(c)}
          </Link>
        ))}
      </Breadcrumbs>
    );
  };

  // Marks a container that is one of several kept in step, so the listing shows at a
  // glance which ones aren't maintained on their own.
  const kitChip = (i) => {
    if (!isKitInstance(i)) return null;
    const n = kitInstances(i.kit_id).length;
    return (
      <Tooltip key="kit"
               title={`One of ${n} ${kitName(i.kit_id)} containers keeping the same required contents`}>
        <Chip size="small" variant="outlined" icon={<LinkIcon />}
              label={`${kitName(i.kit_id)} ×${n}`} />
      </Tooltip>
    );
  };

  // Whether this item has been checked since the last event finished. A tick either way —
  // green when it has, amber when a check is due — so the column reads as one signal
  // rather than as a mark that only appears when something is wrong. Nothing at all until
  // there is a finished event to measure against.
  const checkChip = (i) => {
    const state = checkState(i);
    if (!state) return null;
    const when = i.last_checked_date
      ? `last checked ${i.last_checked_date}${i.last_checked_by ? ` by ${i.last_checked_by}` : ""}`
      : "never checked";
    const verdict = state === "warning" ? "Check due" : "Checked since the last event";
    return (
      <Tooltip key="checked"
               title={`${verdict} — ${when}. ${lastEventName} ended ${checkSince}.`}>
        <TaskAltIcon fontSize="small" color={state} />
      </Tooltip>
    );
  };

  // The item's name in a pill coloured by its type's category — the same colours as the
  // category badges on the Types page, and the same marker in both views. The tooltip names
  // the category, since colour alone isn't much of a signal.
  const nameChip = (i) => {
    const category = typeCategory(i.type_id);
    return (
      <Tooltip title={category || typeName(i.type_id)}>
        <Chip size="small" label={itemLabel(i)}
              color={INVENTORY_CATEGORY_COLOR[category] || "default"} />
      </Tooltip>
    );
  };

  // The columns both views share, after each has led with the name however it wants to.
  // Type, details, size and serial number are all folded into the name.
  const detailCells = (i) => [
    <QuantityCell key="quantity" item={i} />,
    <ExpiryCell key="expiry" item={i} grace={graceFor(i)} />,
  ];

  // A stock cupboard or a pending-disposal container, marked as such in the listing.
  // The role is its type's (set on the Types page).
  const roleChip = (i) => {
    const role = roleOf(i, typeById);
    return role ? (
      <Chip key="role" size="small" variant="outlined" color={role === "quarantine" ? "error" : "primary"}
            label={STOCK_ROLE_LABEL[role]} />
    ) : null;
  };

  const flatCells = (i) => [
    <TableCell key="name">
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        {checkChip(i)}
        {nameChip(i)}
        {kitChip(i)}
        {roleChip(i)}
      </Stack>
    </TableCell>,
    ...detailCells(i),
  ];

  // The Contents column. In the tree a container's own row toggles it open, so editing it
  // needs a button of its own — in the flat list the row click already does that.
  const actionsCell = (i, { withEdit = false } = {}) => (
    <TableCell key="actions" align="right" onClick={e => e.stopPropagation()}>
      {isContainer(i) && (
        <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
          {withEdit && (
            <Tooltip title="Edit item">
              <IconButton size="small" onClick={() => openEdit(i)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Show contents">
            <IconButton size="small" color="primary" onClick={() => openContents(i)}>
              <Inventory2Icon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Straight to counting it, without stopping at the read-only view. Offered for
              an empty container too — items can be added from inside the check. */}
          {canKitCheck && (
            <Tooltip title={`Kit check ${itemLabel(i)}`}>
              <IconButton size="small" color="primary" onClick={() => openCheck(i)}>
                <ChecklistIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </TableCell>
  );

  // A tree row: the same columns as the flat list, with the name indented one level per
  // container above it. A render function rather than a component, matching the rest of
  // this page.
  const treeRow = ({ item: i, children, depth }) => {
    const open = isExpanded(i.id);
    return (
      <TableRow hover key={i.id}
                onClick={() => (children.length > 0 ? toggleExpanded(i.id) : openEdit(i))}>
        {/* Indented by one level per container above this item. useFlexGap because the
            row mixes element types (button, div, span) and Stack's default spacing is a
            sibling selector that skips the first of each. */}
        <TableCell>
          <Stack direction="row" spacing={1} useFlexGap
                 sx={{ alignItems: "center", ml: depth * TREE_INDENT }}>
            {children.length > 0 ? (
              <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}
                          sx={{ width: TOGGLE_WIDTH, height: TOGGLE_WIDTH, flexShrink: 0 }}
                          onClick={e => { e.stopPropagation(); toggleExpanded(i.id); }}>
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              // No toggle: a plain item, an empty container, or one whose contents the
              // filters have hidden. The spacer keeps it in line with its siblings.
              <Box sx={{ width: TOGGLE_WIDTH, flexShrink: 0 }} />
            )}
            {checkChip(i)}
            {nameChip(i)}
            {kitChip(i)}
            {roleChip(i)}
          </Stack>
        </TableCell>
        {detailCells(i)}
        {actionsCell(i, { withEdit: isContainer(i) })}
      </TableRow>
    );
  };

  // What a save will reach beyond the item in front of you, and — for something being
  // added to a kit container — the choice of how far. A render function, like the rest
  // of this page, so typing in the dialog doesn't remount it.
  const kitBanner = () => {
    const stored = editing === "new" ? null : itemById[editing];
    // Leaving the kit is offered right where membership is explained — it's the answer
    // when one container has to stop matching the rest.
    const unlinkAction = can("InventoryItems.edit") ? (
      <Button size="small" color="inherit" startIcon={<LinkOffIcon />}
              onClick={() => setConfirmUnlink(true)}>
        Unlink
      </Button>
    ) : undefined;
    if (isKitInstance(stored)) {
      const n = kitInstances(stored.kit_id).length;
      return (
        <Alert severity="info" icon={<LinkIcon />} action={unlinkAction}>
          One of {n} containers in the <strong>{kitName(stored.kit_id)}</strong> kit. They
          hold the same required contents; what's actually in each of them is its own.
          This container's own name and notes belong to it alone.
        </Alert>
      );
    }
    if (isKitLine(stored)) {
      const others = counterparts(stored);
      const holding = others.filter(c => (c.quantity ?? 0) > 0).length;
      return (
        <Alert severity="info" icon={<LinkIcon />} action={unlinkAction}>
          Shared across {others.length + 1} <strong>{kitName(stored.kit_id)}</strong>{" "}
          containers. Changing the type, details, size or required quantity changes it in
          all of them{holding ? `, ${holding} of which are holding some right now` : ""}.
          The quantity, serial number, expiry date and batches stay with this one.
        </Alert>
      );
    }
    const kitId = targetKitId(editForm);
    if (!kitId) return null;
    const n = kitInstances(kitId).length;
    return (
      <Box>
        <ToggleButtonGroup size="small" exclusive value={scope}
                           onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value={SCOPE_KIT}>All {n} {kitName(kitId)} containers</ToggleButton>
          <ToggleButton value={SCOPE_ONE}>This container only</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {scope === SCOPE_KIT
            ? `Adds it to every ${kitName(kitId)} container — each starting empty — and keeps them in step from now on.`
            : `Leaves the other ${n - 1} ${n === 2 ? "container" : "containers"} alone. Pick this for something only this one carries.`}
        </Typography>
      </Box>
    );
  };

  // How many items, and how many of those are containers — the shape of what a delete
  // or an empty is about to destroy.
  const contentsCount = (containerId) => {
    const inside = descendantsOf(containerId);
    const nested = inside.filter(isContainer).length;
    return {
      total: inside.length,
      phrase: `${inside.length} item${inside.length === 1 ? "" : "s"}`
        + (nested ? `, including ${nested} container${nested === 1 ? "" : "s"}` : ""),
    };
  };

  // Deleting one line of a kit takes it out of every container; deleting one of the
  // containers takes only that one. Either way the contents go with it. Worth spelling
  // all of that out before it happens.
  const deleteWarning = (target) => {
    const parts = [];
    if (isKitLine(target)) {
      const others = counterparts(target);
      const holding = others.filter(c => (c.quantity ?? 0) > 0).length;
      parts.push(`This is one line of the ${kitName(target.kit_id)} kit, so it will be `
        + `removed from all ${others.length + 1} containers`
        + (holding ? `, ${holding} of which are still holding some` : "") + ".");
      parts.push("To take it out of this one only, unlink this container from the kit first.");
    } else if (isKitInstance(target)) {
      const others = kitInstances(target.kit_id).length - 1;
      parts.push(`This deletes this container only, leaving the other ${others} in the `
        + `${kitName(target.kit_id)} kit alone.`);
    }
    const inside = target ? contentsCount(target.id) : { total: 0 };
    if (inside.total > 0) {
      parts.push(`It holds ${inside.phrase}, and everything inside will be deleted with it`
        + (isKitLine(target) ? ", in every container." : "."));
    } else if (parts.length === 0) {
      parts.push("This will permanently delete this item.");
    }
    parts.push("This cannot be undone.");
    return parts.join(" ");
  };

  // Emptying is deliberately local, so a container that shares its contents with others
  // needs to be told what that means for the rest of them.
  const emptyWarning = () => {
    if (!currentItem) return "";
    const inside = contentsCount(currentItem.id);
    const parts = [`This deletes ${inside.phrase} from ${itemLabel(currentItem)}, along with `
      + `anything inside those containers. ${itemLabel(currentItem)} itself stays.`];
    if (currentItem.kit_id) {
      const n = isKitInstance(currentItem)
        ? kitInstances(currentItem.kit_id).length - 1
        : counterparts(currentItem).length;
      parts.push(`These items are shared with ${n} other ${kitName(currentItem.kit_id)} `
        + `container${n === 1 ? "" : "s"}, which keep theirs — this one will be left out of `
        + `step with them until it's filled back in or unlinked.`);
    }
    parts.push("This cannot be undone.");
    return parts.join(" ");
  };

  const current = contentsStack[contentsStack.length - 1] || null;
  // The Stock menu, offered to whoever can do or see any part of it.
  const canStockMenu = ["Stock.receive", "Stock.move", "Stock.dispose", "Stock.viewHistory"]
    .some(perm => can(perm));
  // A kit check counts ordinary items (saved on the item, InventoryItems.edit) and
  // batch-tracked ones (a stock check, Stock.check); either is reason to offer it, and
  // each row is editable only by whoever may save it — see canCheckRow.
  const canKitCheck = can("InventoryItems.edit") || can("Stock.check");
  // Read fresh: the breadcrumb holds a snapshot from when the dialog opened, and a kit
  // action taken inside it changes the stored row.
  const currentItem = current ? (itemById[current.id] ?? current) : null;

  // Both export formats cover what's on screen — same filters, same sort order, and
  // the same display name, so a row in the sheet is recognisable as the row on screen.
  const runExport = (exporter) => {
    setExportMenu(null);
    exporter(visible, {
      typeName, category: typeCategory, location: locationPathString, name: itemLabel,
    });
  };

  // Blocks Save while the form would produce an item indistinguishable from another. A
  // new item excludes nothing, since there is no stored row of its own to skip.
  const editDup = duplicateOf(editForm, editing === "new" ? null : editing);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Inventory</Typography>
        {can("InventoryTypes.view") && (
          <Button variant="outlined" startIcon={<CategoryIcon />} sx={{ ml: "auto" }}
                  onClick={() => navigate("/inventory-types")}>
            Manage types
          </Button>
        )}
        {/* Every allocation still out, on any event — the one view of them that
            isn't inside an item, a person or an event. */}
        <Button variant="outlined" startIcon={<AssignmentReturnIcon />}
                onClick={() => setOutstandingOpen(true)}
                sx={can("InventoryTypes.view") ? undefined : { ml: "auto" }}>
          Not returned
        </Button>
        <OutstandingAllocationsDialog open={outstandingOpen}
                                      onClose={() => setOutstandingOpen(false)} />
        {/* Batch-tracked stock: the operations that change it, and the views of it. */}
        {canStockMenu && (
          <>
            <Button variant="outlined" startIcon={<MedicationIcon />} endIcon={<ExpandMoreIcon />}
                    onClick={e => setStockMenu(e.currentTarget)}>
              Stock
            </Button>
            <Menu open={Boolean(stockMenu)} anchorEl={stockMenu} onClose={() => setStockMenu(null)}>
              {can("Stock.receive") && (
                <MenuItem onClick={() => { setStockMenu(null); setReceiveOpen(true); }}>
                  <ListItemIcon><LocalShippingIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Receive a delivery" secondary="Book stock into a cupboard" />
                </MenuItem>
              )}
              {can("Stock.move") && (
                <MenuItem onClick={() => { setStockMenu(null); setMoveInit({}); }}>
                  <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Move stock" secondary="Between cupboards, bags and pouches" />
                </MenuItem>
              )}
              {can("Stock.move") && (
                <MenuItem onClick={() => { setStockMenu(null); setMoveInit({ quarantine: true }); }}>
                  <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Set aside for disposal" secondary="Expired, damaged or recalled" />
                </MenuItem>
              )}
              {can("Stock.dispose") && (
                <MenuItem onClick={() => { setStockMenu(null); setDisposeOpen(true); }}>
                  <ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Dispose of stock" secondary="With the waste transfer note" />
                </MenuItem>
              )}
              {can("Stock.viewHistory") && <Divider />}
              {can("Stock.viewHistory") && (
                <MenuItem onClick={() => navigate("/stock-report")}>
                  <ListItemIcon><AssessmentIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Stock report" secondary="Shortages, expiries and recalls" />
                </MenuItem>
              )}
              {can("Stock.viewHistory") && (
                <MenuItem onClick={() => navigate("/stock-history")}>
                  <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Stock history" secondary="Every batch, every change" />
                </MenuItem>
              )}
            </Menu>
            <ReceiveStockDialog open={receiveOpen} onClose={() => setReceiveOpen(false)}
                                onDone={reload} items={items} types={types} />
            <DisposeStockDialog open={disposeOpen} onClose={() => setDisposeOpen(false)}
                                onDone={reload} items={items} types={types} me={me} />
          </>
        )}
        {/* Also opened from an item's batches and from the kit check, so it isn't
            inside the menu's gate. */}
        <MoveStockDialog open={moveInit !== null} onClose={() => setMoveInit(null)}
                         initial={moveInit} items={items} types={types}
                         onDone={() => {
                           const row = moveInit?.rowId;
                           reload().then(() => row != null && dropDraft(row));
                         }} />
        {can("InventoryItems.export") && (
          <>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} endIcon={<ExpandMoreIcon />}
                    disabled={visible.length === 0}
                    onClick={e => setExportMenu(e.currentTarget)}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Export to Excel
            </Button>
            <Menu open={Boolean(exportMenu)} anchorEl={exportMenu} onClose={() => setExportMenu(null)}>
              <MenuItem onClick={() => runExport(exportInventoryItems)}>
                <ListItemText primary="One row per item"
                              secondary="Totals only, no batch detail" />
              </MenuItem>
              <MenuItem onClick={() => runExport(exportInventoryBatches)}>
                <ListItemText primary="One row per batch"
                              secondary="A row for each pack, item details repeated" />
              </MenuItem>
            </Menu>
          </>
        )}
        {can("InventoryItems.add") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add item</Button>
        )}
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Filter by category" select size="small" sx={{ minWidth: 160 }} value={categoryFilter}
                   onChange={e => setCategoryFilter(e.target.value)}>
          <MenuItem value=""><em>All categories</em></MenuItem>
          {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label="Filter by name" size="small" value={nameFilter}
                   onChange={e => setNameFilter(e.target.value)} />
        <TextField label="Filter by location" size="small" value={locationFilter}
                   onChange={e => setLocationFilter(e.target.value)} />
        {/* Narrow to what needs restocking, replacing or checking. Not `exclusive`, so the
            value is the array of selected issues and they can be on at once — an item
            qualifies if it has any of the problems asked for. */}
        <ToggleButtonGroup size="small" color="warning" value={issues}
                           onChange={(_, v) => setIssues(v)}>
          {issueOptions.map(issue => (
            <ToggleButton key={issue} value={issue}>
              <Tooltip title={issueHint(issue)}>
                <Box component="span">{ISSUE_LABEL[issue]}</Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField label="Sort by" select size="small" sx={{ minWidth: 150 }} value={sortBy}
                   onChange={e => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {/* Same items, sorted and filtered the same way — flat, or nested by container. */}
        <ToggleButtonGroup size="small" exclusive value={view}
                           onChange={(_, v) => v && setView(v)}>
          <ToggleButton value={VIEW_FLAT} aria-label="Flat list">
            <Tooltip title="Flat list"><ViewListIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value={VIEW_TREE} aria-label="Tree view">
            <Tooltip title="Tree — items nested inside their container">
              <AccountTreeIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {visible.length} of {items.length}
        </Typography>
      </Stack>

      {/* List */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Name</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell align="right">Contents</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* The tree shows where each item sits by nesting it, so no Location line. */}
              {view === VIEW_TREE && treeVisible.map(treeRow)}
              {view === VIEW_FLAT && visible.map(i => {
                // A long container trail wraps badly inside a column, so it gets its own
                // full-width line under the item — and only when there is one to show.
                const filed = locationPath(i).length > 0;
                return [
                  <TableRow hover key={i.id} onClick={() => openEdit(i)}
                            sx={filed ? { "& td": { borderBottom: "none", pb: 0.5 } } : undefined}>
                    {flatCells(i)}
                    {actionsCell(i)}
                  </TableRow>,
                  filed && (
                    <TableRow key={`${i.id}-location`} onClick={() => openEdit(i)}>
                      <TableCell colSpan={ITEM_COLUMNS} sx={{ pt: 0 }}>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                          <Typography variant="caption" color="text.secondary">Location</Typography>
                          <LocationCrumbs item={i} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ),
                ];
              })}
              {(view === VIEW_TREE ? treeVisible.length : visible.length) === 0 && (
                <TableRow>
                  <TableCell colSpan={ITEM_COLUMNS}
                             align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? <CircularProgress size={28} />
          : items.length === 0 ? "No items yet — add one." : "No items match your filters."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / edit dialog */}
      <Dialog open={editing !== null} onClose={requestClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>
          {editing === "new" ? "Add item" : `Edit ${itemLabel(editForm)}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Kits: what this save reaches beyond the item itself. */}
            {kitBanner()}
            {typePicker({
              fullWidth: true, value: editForm.type_id, focus: editing === "new",
              onPick: id => setEditForm({ ...editForm, type_id: id }),
            })}
            {!isBatched(editForm) && (
              <TextField label="Serial number" fullWidth value={editForm.serial_number}
                         onChange={e => setEditForm({ ...editForm, serial_number: e.target.value })} />
            )}
            <TextField label="Details" fullWidth value={editForm.details}
                       error={Boolean(editDup)}
                       helperText={editDup ? duplicateHint(editDup, editForm) : undefined}
                       onChange={e => setEditForm({ ...editForm, details: e.target.value })} />
            <TextField label="Size" fullWidth value={editForm.size}
                       onChange={e => setEditForm({ ...editForm, size: e.target.value })} />
            {!isBatched(editForm) && (
              <TextField label="Quantity" type="number" fullWidth value={editForm.quantity}
                         onChange={e => setEditForm({ ...editForm, quantity: e.target.value })} />
            )}
            {/* Every category, not only the batched ones: it's what "short" is measured
                against for any item, and what a container's printed contents list gives as
                the number to pack. */}
            <TextField label="Required quantity" type="number" fullWidth
                       value={editForm.required_quantity}
                       helperText={isBatched(editForm)
                         ? "Stock level to keep — flagged red when the batches held fall below it"
                         : "Stock level to keep — flagged red when the quantity falls below it, and printed on the contents list"}
                       onChange={e => setEditForm({ ...editForm, required_quantity: e.target.value })} />
            {locationPicker({
              fullWidth: true, value: editForm.location_id,
              excludeId: editing === "new" ? null : editing, // a new item can't be its own container
              helperText: "Pick a container item that holds this item",
              onPick: id => setEditForm({ ...editForm, location_id: id }),
            })}
            {isBatched(editForm) ? (
              // Read-only: a batch's quantity changes only through a stock operation,
              // so the ledger always says why. Each row offers those instead.
              editing === "new" ? (
                <Typography variant="body2" color="text.secondary">
                  Drugs and medical stock is held in batches, and every change to it is
                  recorded. Save the item, then book a delivery in or move stock here.
                </Typography>
              ) : (
                <HoldingsPanel
                  item={itemById[editing]} grace={graceFor(editForm)}
                  required={editForm.required_quantity}
                  canMove={can("Stock.move")} canHistory={can("Stock.viewHistory")}
                  onMove={h => setMoveInit({ lines: [{ holding_id: h.id, quantity: h.quantity }] })}
                  onQuarantine={h => setMoveInit({
                    quarantine: true,
                    lines: [{ holding_id: h.id, quantity: h.quantity }],
                    reason: expiryFlag(h.expiry_date, graceFor(editForm))?.severity === "error"
                      ? "Expired" : (h.recalled ? "Recalled by supplier" : ""),
                  })}
                  onHistory={({ item, batch }) => navigate(batch
                    ? `/stock-history?batch_id=${batch.batch_id}`
                    : `/stock-history?item_id=${item.id}`)}
                  onChanged={reload} />
              )
            ) : (
              // Flagged against the type's grace, and — while it's still inside one —
              // the field says so, since nothing else on the form would explain a date
              // long past that isn't in red.
              <TextField label="Expiry date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                         value={editForm.expiry_date}
                         {...expiryFieldProps(editForm.expiry_date, graceFor(editForm),
                                              graceHint(editForm.expiry_date, graceFor(editForm)))}
                         onChange={e => setEditForm({ ...editForm, expiry_date: e.target.value })} />
            )}
            <TextField label="Last checked date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                       value={editForm.last_checked_date}
                       onChange={e => setEditForm({ ...editForm, last_checked_date: e.target.value })} />
            <TextField label="Last checked by" fullWidth value={editForm.last_checked_by}
                       onChange={e => setEditForm({ ...editForm, last_checked_by: e.target.value })} />
            <TextField label="Notes" fullWidth multiline value={editForm.notes}
                       onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />

            {/* Where this item has been issued to, most recent event first. Rows
                save as you go, so Save and Cancel below apply only to the item's
                own fields — nothing here. Hidden while adding: an allocation needs
                an item to point at. */}
            {editing !== "new" && can("InventoryItems.view") && (
              <>
                <Divider textAlign="left">Allocations</Divider>
                <Typography variant="caption" color="text.secondary">
                  {allocatable(editForm)
                    ? "Who has had this item, and for which event. Saved as you go — "
                      + "Save below applies to the item's fields only."
                    : "This item's type isn't marked as allocatable, so it can't be "
                      + "issued to anyone. Tick \"Can be allocated\" on the type to "
                      + "change that."}
                </Typography>
                <AllocationManager
                  itemId={editing}
                  canAdd={allocatable(editForm) && can("InventoryItems.add")}
                  canEdit={can("InventoryItems.edit")}
                  canDelete={can("InventoryItems.delete")} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editing !== "new" && can("InventoryItems.delete") && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Nothing has been checked on an item that doesn't exist yet. */}
          {editing !== "new" && can("InventoryItems.edit") && (
            <Tooltip title={`Records ${me.name || me.email || "you"} as having checked this today, and saves`}>
              <span>
                <Button variant="outlined"
                        startIcon={savingItem ? <CircularProgress size={16} /> : <TaskAltIcon />}
                        disabled={!canSave(editForm) || Boolean(editDup) || savingItem}
                        onClick={markChecked}>
                  Checked
                </Button>
              </span>
            </Tooltip>
          )}
          <Button onClick={requestClose}>Cancel</Button>
          {(editing === "new" ? can("InventoryItems.add") : can("InventoryItems.edit")) && (
            <Button variant="contained"
                    disabled={!canSave(editForm) || Boolean(editDup) || savingItem}
                    startIcon={savingItem ? <CircularProgress size={16} /> : undefined}
                    onClick={() => saveEdit()}>{savingItem ? "Saving…" : "Save"}</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {editing !== null && itemById[editing] ? itemLabel(itemById[editing]) : "item"}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteWarning(editing === null ? null : itemById[editing])}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={removeItem}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick-add type, from typing an unknown type into the Type picker */}
      <Dialog open={newType !== null} onClose={() => setNewType(null)} fullWidth maxWidth="xs">
        <DialogTitle>Add inventory type</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth autoFocus value={newType?.name ?? ""}
                       onChange={e => setNewType({ ...newType, name: e.target.value })} />
            <TextField label="Category" select fullWidth value={newType?.category ?? ""}
                       helperText={categoryHint(newType?.category) || "Required"}
                       onChange={e => setNewType({ ...newType, category: e.target.value })}>
              {INVENTORY_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              Notes, expiry grace and reorder details can be added on the types page.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewType(null)}>Cancel</Button>
          <Button variant="contained"
                  disabled={!(newType?.name || "").trim() || !newType?.category || savingItem}
                  startIcon={savingItem ? <CircularProgress size={16} /> : undefined}
                  onClick={saveNewType}>
            Save type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this item. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={closeEdit}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Contents dialog: a read-only view, or the kit check (wider, and editable) */}
      <Dialog open={contentsStack.length > 0} onClose={check ? requestExitCheck : closeContents}
              fullWidth maxWidth={check ? "md" : "sm"} fullScreen={fullScreen}>
        <DialogTitle sx={{ pb: 1 }}>
          {check ? "Kit check" : "Contents"}
          <Breadcrumbs separator="›" sx={{ mt: 0.5, fontSize: 14 }}>
            {contentsStack.map((c, idx) =>
              // Mid-check the trail is text only: jumping elsewhere would abandon a draft
              // that belongs to this container.
              idx === contentsStack.length - 1 || check ? (
                <Typography key={c.id}
                            color={idx === contentsStack.length - 1 ? "text.primary" : "text.secondary"}
                            sx={{ fontSize: 14, fontWeight: idx === contentsStack.length - 1 ? 600 : 400 }}>
                  {itemLabel(c)}
                </Typography>
              ) : (
                <Link key={c.id} component="button" type="button" underline="hover"
                      sx={{ fontSize: 14 }} onClick={() => jumpTo(idx)}>
                  {itemLabel(c)}
                </Link>
              )
            )}
          </Breadcrumbs>
          {/* Whether this container is maintained on its own or as one of a family. */}
          {currentItem?.kit_id && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {isKitInstance(currentItem)
                ? `One of ${kitInstances(currentItem.kit_id).length} ${kitName(currentItem.kit_id)} containers — the required contents below are shared by all of them.`
                : `Part of the ${kitName(currentItem.kit_id)} kit — the contents below are shared with the matching container in every instance.`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {check ? (
            <>
              {/* Finding one row in a bag of forty, and adding what turns out to be
                  missing — both belong in the check, not in a trip back to the list. */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                     sx={{ alignItems: { sm: "center" }, mb: 2 }}>
                <TextField label="Filter items" size="small" value={checkFilter}
                           sx={{ flex: 1 }}
                           onChange={e => setCheckFilter(e.target.value)} />
                {can("InventoryItems.add") && (
                  <Tooltip title={`Add an item to ${itemLabel(current)}`}>
                    <Button startIcon={<AddIcon />} sx={{ flexShrink: 0 }}
                            onClick={() => openNewIn(current.id)}>
                      Add item
                    </Button>
                  </Tooltip>
                )}
              </Stack>
              {kitCheckTable()}
            </>
          ) : current && contents(current.id).length > 0 ? (
            <List dense>
              {contents(current.id).map(c => {
                const container = isContainer(c);
                const inner = (
                  <ListItemText
                    primary={itemLabel(c)}
                    secondary={[
                      container ? `${contents(c.id).length} inside` : null,
                      // Something one container carries that the rest of the kit doesn't.
                      isExtra(c) ? "not part of the kit" : null,
                    ].filter(Boolean).join(" · ") || null}
                  />
                );
                return container ? (
                  <ListItem key={c.id} disablePadding
                            secondaryAction={<Chip size="small" label={`Qty ${c.quantity}`} color={isShort(c) ? "error" : "default"} />}>
                    <ListItemButton onClick={() => drillInto(c)}>
                      <ListItemIcon sx={{ minWidth: 36 }}><Inventory2Icon color="primary" fontSize="small" /></ListItemIcon>
                      {inner}
                      <ChevronRightIcon color="action" />
                    </ListItemButton>
                  </ListItem>
                ) : (
                  <ListItem key={c.id} secondaryAction={<Chip size="small" label={`Qty ${c.quantity}`} color={isShort(c) ? "error" : "default"} />}>
                    {inner}
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              This container is empty.
            </Typography>
          )}
        </DialogContent>
        {/* Wraps: the container actions add up, and DialogActions is a single flex row. */}
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", rowGap: 1 }}>
          {check ? (
            <>
              {/* Progress is against the whole container, whatever the filter is showing —
                  so a narrowed list can't read as a finished check. */}
              <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
                {checkedCount()} of {contents(current.id).length} items checked
                {checkRows().length < contents(current.id).length
                  ? ` · ${checkRows().length} shown` : ""}
              </Typography>
              <Button onClick={requestExitCheck} disabled={saving}>Close</Button>
              <Button onClick={saveCounts} disabled={saving || !checkDirty()}>Save counts</Button>
              <Tooltip title={allChecked()
                ? `Records ${itemLabel(current || {})} itself as checked today`
                : "Check every item first"}>
                <span>
                  <Button variant="contained" startIcon={<TaskAltIcon />}
                          disabled={saving || !allChecked()} onClick={markContainerChecked}>
                    Kit checked
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <>
              {/* Offered for an empty container as well: the check is where missing items
                  get added, so it's the way to fill a new bag as much as to count a packed
                  one. */}
              {canKitCheck && current && (
                <Button variant="outlined" startIcon={<ChecklistIcon />} onClick={startCheck}>
                  Kit check
                </Button>
              )}
              {/* The printed list for this container, pockets and all — the sheet that
                  lives in the bag. Gated like the Excel exports. */}
              {can("InventoryItems.export") && current && contents(current.id).length > 0 && (
                <Tooltip title="Printable list of what should be in here, and how many">
                  <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
                          onClick={() => exportContents(current)}>
                    Contents list PDF
                  </Button>
                </Tooltip>
              )}
              {/* A sticker for the outside of the container, so the list above can be
                  reached by anyone holding it — no sign-in, since whoever scanned it can
                  already see inside. Offered for an empty container too: the code is
                  printed once and the bag gets packed afterwards. */}
              {can("InventoryItems.export") && currentItem && (
                <Tooltip title="Printable code that opens this contents list when scanned">
                  <Button variant="outlined" startIcon={<QrCode2Icon />} disabled={qrBusy}
                          onClick={() => openQr(currentItem)}>
                    QR code
                  </Button>
                </Tooltip>
              )}
              {/* Kit actions live here rather than in the item dialog: they're things you
                  do to a container, and there are no half-finished edits to lose. */}
              {can("InventoryItems.add") && currentItem && (
                <Tooltip title="Copy this container's required contents into new containers">
                  <Button variant="outlined" startIcon={<ContentCopyIcon />}
                          onClick={() => setDuplicating(currentItem)}>
                    Duplicate
                  </Button>
                </Tooltip>
              )}
              {can("InventoryItems.edit") && currentItem && (
                <Tooltip title={isKitInstance(currentItem)
                  ? "Compare another container against this kit and bring it in"
                  : "Compare this against containers that should hold the same things, then keep them in step"}>
                  <Button variant="outlined" startIcon={<LinkIcon />}
                          onClick={() => setLinking(currentItem)}>
                    {isKitInstance(currentItem) ? "Add to this kit" : "Link identical"}
                  </Button>
                </Tooltip>
              )}
              {/* Clear the container out without deleting the container itself. */}
              {can("InventoryItems.delete") && currentItem && contents(currentItem.id).length > 0 && (
                <Tooltip title="Delete everything inside this container, keeping the container">
                  <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />}
                          onClick={() => setConfirmEmpty(true)}>
                    Empty
                  </Button>
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={closeContents}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation for a row of the kit check. Separate from the item dialog's
          own, since that one keys off `editing` and the check has no item open. */}
      <Dialog open={confirmDeleteRow !== null} onClose={() => setConfirmDeleteRow(null)}>
        <DialogTitle>
          Delete {confirmDeleteRow ? itemLabel(confirmDeleteRow) : "item"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteWarning(confirmDeleteRow)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteRow(null)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={removeCheckRow}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty-container confirmation */}
      <Dialog open={confirmEmpty} onClose={() => setConfirmEmpty(false)}>
        <DialogTitle>
          Delete everything inside {currentItem ? itemLabel(currentItem) : "this container"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{emptyWarning()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEmpty(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteSweepIcon />}
                  onClick={emptyContainer}>
            Delete contents
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR code for a container. The address is shown before anything is downloaded on
          purpose: it comes from the backend's own origin, so a code generated against a
          local or staging backend would print a link that goes nowhere — and it is about
          to be printed onto something that gets stuck on a bag for years. */}
      <Dialog open={Boolean(qr)} onClose={closeQr} fullWidth maxWidth="sm">
        <DialogTitle>
          QR code for {qr && itemById[qr.itemId] ? itemLabel(itemById[qr.itemId]) : "this container"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scanning this code opens the contents list for this container — what should be
            in it, and how many. It needs no sign-in, since anyone who can scan it is
            already holding the container; nothing else in the inventory can be reached
            from it. The code keeps working when the container is renamed, moved or
            repacked.
          </DialogContentText>
          {qr && qrOwn() && (
            <Alert severity="info"
                   action={
                     <Button color="inherit" size="small" startIcon={<ContentCopyIcon />}
                             onClick={() => {
                               navigator.clipboard?.writeText(qrOwn().url);
                               notify("Link copied.", "success");
                             }}>
                       Copy
                     </Button>}>
              <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                {qrOwn().url}
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Check that address is the one you want before printing — it can't be changed
            once the code is on the bag.
          </Typography>
          {qr && qr.codes.length > 1 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              The other {qr.codes.length - 1} containers in the{" "}
              {kitName(itemById[qr.itemId]?.kit_id)} kit have their own codes. Downloading
              them all gives a zip of one PNG per container, each named after it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQr} disabled={qrBusy}>Close</Button>
          {qr && qr.codes.length > 1 && (
            <Button startIcon={<QrCode2Icon />} disabled={qrBusy}
                    onClick={() => saveQr(qr.codes, kitName(itemById[qr.itemId]?.kit_id))}>
              Download all {qr.codes.length} (.zip)
            </Button>
          )}
          <Button variant="contained" startIcon={<QrCode2Icon />} disabled={qrBusy || !qr}
                  onClick={() => saveQr([qrOwn()])}>
            Download PNG
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink-from-kit confirmation. Nothing is deleted, so the risk is only that the
          containers stop being kept in step — worth saying, since the button sits in an
          informational banner. */}
      <Dialog open={confirmUnlink} onClose={() => setConfirmUnlink(false)}>
        <DialogTitle>Stop keeping this in step?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {editing !== null && isKitInstance(itemById[editing])
              ? `This container and everything in it leave the ${kitName(itemById[editing].kit_id)} kit. Nothing is deleted and no quantities change — it just stops matching the others, and changes made to them will no longer reach it.`
              : `This item stops being shared with the other containers. Nothing is deleted, and the copies in the other containers stay where they are.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnlink(false)}>Keep it linked</Button>
          <Button color="warning" variant="contained" startIcon={<LinkOffIcon />}
                  onClick={unlinkFromKit}>
            Unlink
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy a container's required contents into new containers, optionally as a kit */}
      <DuplicateContainerDialog
        open={duplicating !== null}
        source={duplicating}
        sourceLabel={duplicating ? itemLabel(duplicating) : ""}
        items={items}
        kitName={kitName}
        onClose={() => setDuplicating(null)}
        onDone={() => { reload(); setDuplicating(null); }}
      />

      {/* Compare containers that already exist and link them, filling in what's missing */}
      <KitLinkDialog
        open={linking !== null}
        source={linking}
        containers={items.filter(isContainer)}
        pathLabel={itemPathLabel}
        kitName={kitName}
        onClose={() => setLinking(null)}
        onDone={reload}
      />

      {/* Abandon-kit-check confirmation */}
      <Dialog open={confirmExitCheck} onClose={() => setConfirmExitCheck(false)}>
        <DialogTitle>Leave the kit check?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The items you've ticked off are already saved. You've also changed counts on items
            you haven't ticked — those will be lost unless you save them first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExitCheck(false)}>Keep checking</Button>
          <Button color="error" variant="contained" onClick={exitCheck}>Discard those counts</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
